<template>
	<el-dialog title="我的用餐统计" :visible.sync="visible" width="720px" custom-class="meal-cal-dialog"
		append-to-body>
		<div class="mc-wrap" v-loading="loading">
			<div class="mc-head">
				<div class="mc-nav">
					<i class="el-icon-arrow-left" @click="shiftMonth(-1)"></i>
					<span class="mc-title">{{ year }} 年 {{ month }} 月</span>
					<i class="el-icon-arrow-right" @click="shiftMonth(1)"></i>
					<span class="mc-today-btn" @click="goToday">今天</span>
				</div>
				<div class="mc-balance" v-if="info" :class="{ neg: Number(info.balance) < 0 }">
					余额 ¥{{ fmt(info.balance) }}
					<span class="mc-recharge-btn" @click="openRecharge">充值</span>
				</div>
			</div>
			<!-- 一人多档(如 牛牛/牛牛仔 共用一个账号)时显示人名页签切换 -->
			<div class="mc-emps" v-if="empList.length > 1">
				<span class="mc-emps-label">查看：</span>
				<span v-for="e in empList" :key="e.id" class="mc-emp-tab" :class="{ on: e.id === currentEmpId }"
					@click="switchEmp(e)">{{ e.name }}</span>
			</div>
			<!-- 操作指引(固定常驻,教用户怎么点) -->
			<div class="mc-tip">
				🍚 点格子里的「中 / 晚」标记这餐<b>不吃</b>（变 ❌），再点一下取消；✅ 是已登记就餐；当天中餐 9:00 前、晚餐 14:00 前可改，超时锁定。
			</div>
			<div class="mc-weeks">
				<span v-for="w in ['日', '一', '二', '三', '四', '五', '六']" :key="w">{{ w }}</span>
			</div>
			<div class="mc-grid">
				<div v-for="(c, i) in cells" :key="i" class="mc-cell" :class="{ dim: !c.inMonth, today: c.isToday }">
					<div class="mc-day">{{ c.day }}</div>
					<template v-if="c.inMonth">
						<span class="mc-meal" :class="mealCls(c, 1)" :title="mealTitle(c, 1)"
							@click="onMeal(c, 1)">中 {{ mealTxt(c, 1) }}</span>
						<span class="mc-meal" :class="mealCls(c, 2)" :title="mealTitle(c, 2)"
							@click="onMeal(c, 2)">晚 {{ mealTxt(c, 2) }}</span>
					</template>
				</div>
			</div>
			<div class="mc-foot" v-if="info">
				本月中餐 {{ info.lunchCnt }} 次、晚餐 {{ info.dinnerCnt }} 次，餐费 ¥{{ fmt(info.monthFee) }}
				<span class="mc-sep">｜</span>
				累计餐费 ¥{{ fmt(info.mealTotal) }}，已付 ¥{{ fmt(info.payTotal) }}
				<span v-if="Number(info.balance) < 0" class="mc-neg">（已欠费，请及时缴费）</span>
			</div>
			<div class="mc-empty" v-if="!loading && errorMsg">{{ errorMsg }}</div>
		</div>
		<!-- 自助充值弹窗: step1扫码付款 → step2填金额+传付款截图(未提交禁止关闭) → step3完成 -->
		<el-dialog title="餐费充值" :visible.sync="rcVisible" width="460px" custom-class="meal-rc-dialog"
			append-to-body :show-close="rcStep !== 2" :close-on-click-modal="rcStep !== 2"
			:close-on-press-escape="rcStep !== 2">
			<div v-if="rcStep === 1" class="rc-body">
				<img class="rc-qr" :src="($publicPath || '/') + 'meal-pay-qrcode.jpg'" alt="收款二维码" />
				<div class="rc-tip">
					① 微信扫一扫上方二维码付款（金额自定）<br />② 付款成功后<b>截图保存付款凭证</b><br />③ 点下方按钮上传截图申报（截图在手机上也能直接传）
				</div>
				<el-button type="primary" class="rc-btn" @click="enterStep2">我已付款，去上传截图</el-button>
				<div class="rc-history" v-if="rcList.length">
					<div class="rc-his-title">我的充值记录</div>
					<div v-for="r in rcList" :key="r.id" class="rc-his-row">
						<span class="rc-his-time">{{ r.createdTime }}</span>
						<span class="rc-his-amt">¥{{ fmt(r.amount) }}</span>
						<span class="rc-his-st" :class="'st' + r.status">{{ rcStatusText(r) }}</span>
					</div>
				</div>
			</div>
			<div v-else-if="rcStep === 2" class="rc-body">
				<div class="rc-tip warn">⚠️ 截图提交前本窗口不能关闭</div>
				<div class="rc-field">
					<label>付款金额(元)</label>
					<el-input v-model="rcAmount" type="number" min="1" placeholder="如 200" size="small"></el-input>
				</div>
				<div class="rc-upload-row">
					<div class="rc-upload-col">
						<file-upload :action="'/image/upload'" :isPermanent="true" :maxSize="10 * 1024 * 1024"
							:fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp']" :showLoading="true"
							@success="onRcProofOk" @fail="onRcProofFail">
							<div class="rc-upload">
								<img v-if="rcProofUrl" :src="rcProofUrl" class="rc-proof" />
								<template v-else><i class="el-icon-plus"></i><span>电脑选择截图</span></template>
							</div>
						</file-upload>
						<div class="rc-col-tip">方式一：截图已在电脑上</div>
					</div>
					<div class="rc-upload-col">
						<div class="rc-upload rc-mup">
							<img v-if="mupQrUrl" :src="mupQrUrl" class="rc-mup-qr" alt="手机上传二维码" />
							<span v-else class="rc-mup-tip">二维码<br />加载中…</span>
						</div>
						<div class="rc-col-tip">方式二：截图在手机上<br />微信扫一扫，手机直接传</div>
					</div>
				</div>
				<el-button type="primary" class="rc-btn" :disabled="!rcProofUrl || !rcAmount || rcSubmitting"
					:loading="rcSubmitting" @click="submitRecharge">上传完成，提交审核</el-button>
			</div>
			<div v-else class="rc-body">
				<div class="rc-done">✅ 已提交，等待管理员审核<br />审核通过后自动充值到账，结果会私聊通知你</div>
				<el-button type="primary" class="rc-btn" @click="rcVisible = false">完成</el-button>
			</div>
		</el-dialog>
	</el-dialog>
</template>

<script>
import mealApi from '../../api/mealApi.js'
import FileUpload from '../common/FileUpload.vue'

export default {
	name: 'MealCalendar',
	components: { FileUpload },
	data() {
		return {
			visible: false,
			loading: false,
			errorMsg: '',
			year: 0,
			month: 0,
			todayStr: '',
			empList: [], // 名下员工档案(一个账号可挂多人)
			currentEmpId: null, // 当前查看的档案id
			info: null, // {days, lunchCnt, dinnerCnt, monthFee, mealTotal, payTotal, balance}
			rcVisible: false, // 充值弹窗
			rcStep: 1, // 1扫码 2传截图(禁关闭) 3完成
			rcAmount: '',
			rcProofUrl: '',
			rcSubmitting: false,
			rcList: [], // 充值申请记录
			mupSid: '', // 手机扫码上传会话id
			mupTimer: null // 扫码上传轮询定时器
		}
	},
	computed: {
		$publicPath() { return process.env.BASE_URL || '/'; },
		// 手机扫码上传二维码(后端生成PNG,内容为 /meal-up.html?sid=xxx)
		mupQrUrl() { return this.mupSid ? '/api/public/meal/mup/qr?sid=' + this.mupSid : ''; },
		cells() {
			const first = new Date(this.year, this.month - 1, 1);
			const offset = first.getDay(); // 周日开头
			const daysInMonth = new Date(this.year, this.month, 0).getDate();
			const prevDays = new Date(this.year, this.month - 1, 0).getDate();
			const arr = [];
			for (let i = offset - 1; i >= 0; i--) {
				arr.push({ day: prevDays - i, inMonth: false });
			}
			for (let d = 1; d <= daysInMonth; d++) {
				const ds = this.year + '-' + String(this.month).padStart(2, '0') + '-' + String(d).padStart(2, '0');
				arr.push({ day: d, inMonth: true, dateStr: ds, isToday: ds === this.todayStr });
			}
			let next = 1;
			while (arr.length % 7 !== 0) {
				arr.push({ day: next++, inMonth: false });
			}
			return arr;
		}
	},
	methods: {
		open() {
			this.visible = true;
			this.goToday();
		},
		goToday() {
			const now = new Date();
			this.todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
			this.year = now.getFullYear();
			this.month = now.getMonth() + 1;
			this.load();
		},
		shiftMonth(delta) {
			let m = this.month + delta, y = this.year;
			if (m < 1) { m = 12; y--; }
			if (m > 12) { m = 1; y++; }
			this.year = y;
			this.month = m;
			this.load();
		},
		load() {
			this.loading = true;
			this.errorMsg = '';
			const ym = this.year + '-' + String(this.month).padStart(2, '0');
			mealApi.myMonth(ym, this.currentEmpId).then((data) => {
				this.info = data;
				this.empList = data.emps || [];
				this.currentEmpId = data.empId;
			}).catch((e) => {
				this.info = null;
				this.errorMsg = (e && e.message) || '加载失败';
			}).finally(() => {
				this.loading = false;
			});
		},
		switchEmp(e) {
			if (e.id === this.currentEmpId) return;
			this.currentEmpId = e.id;
			this.load();
		},
		stateOf(ds, mt) {
			const day = this.info && this.info.days && this.info.days[ds];
			const cell = day && day[String(mt)];
			return cell ? cell.state : 0;
		},
		priceOf(ds, mt) {
			const day = this.info && this.info.days && this.info.days[ds];
			const cell = day && day[String(mt)];
			return cell && cell.price != null ? cell.price : null;
		},
		mealTxt(c, mt) {
			const st = this.stateOf(c.dateStr, mt);
			if (st === 1) return '✅';
			if (st === 2) return '❌';
			return this.isLocked(c.dateStr, mt) ? '·' : '+';
		},
		mealCls(c, mt) {
			const st = this.stateOf(c.dateStr, mt);
			if (st === 1) return 'st-eat';
			if (st === 2) return 'st-skip';
			return this.isLocked(c.dateStr, mt) ? 'st-past' : 'st-add';
		},
		mealTitle(c, mt) {
			const name = mt === 1 ? '中餐' : '晚餐';
			const st = this.stateOf(c.dateStr, mt);
			if (st === 1) return name + '已就餐 ¥' + this.fmt(this.priceOf(c.dateStr, mt)) + '，点一下标记不吃';
			if (st === 2) return name + '已标记不吃，点一下取消';
			if (this.isLocked(c.dateStr, mt)) {
				if (c.dateStr < this.todayStr) return name + '未登记（过去的日期不能改）';
				return name + '未登记（已过 ' + (mt === 1 ? '9:00' : '14:00') + ' 截止时间）';
			}
			return '点一下标记' + name + '不吃';
		},
		onMeal(c, mt) {
			if (this.isLocked(c.dateStr, mt)) {
				this.$message.info(c.dateStr < this.todayStr ? '过去的日期不能修改' : '已过截止时间，不能修改');
				return;
			}
			const st = this.stateOf(c.dateStr, mt);
			const cancel = st === 2;
			mealApi.skip({ date: c.dateStr, mealType: mt, cancel: cancel, empId: this.currentEmpId }).then(() => {
				const days = this.info.days || {};
				if (!days[c.dateStr]) this.$set(days, c.dateStr, {});
				this.$set(days[c.dateStr], String(mt), { state: cancel ? 0 : 2 });
				this.$set(this.info, 'days', days);
				this.$message.success((this.info.empName ? '「' + this.info.empName + '」' : '') + (cancel ? '已取消不吃标记' : '已标记不吃'));
			}).catch(() => { });
		},
		isLocked(dateStr, mt) {
			if (dateStr < this.todayStr) return true;
			if (dateStr !== this.todayStr) return false;
			const hour = new Date().getHours();
			if (mt === 1 && hour >= 9) return true;
			if (mt === 2 && hour >= 14) return true;
			return false;
		},
		fmt(v) {
			const n = Number(v || 0);
			return n % 1 === 0 ? String(n) : n.toFixed(2);
		},
		// ----- 自助充值 -----
		openRecharge() {
			this.rcVisible = true;
			this.rcStep = 1;
			this.rcAmount = '';
			this.rcProofUrl = '';
			this.stopMupPoll();
			this.mupSid = '';
			this.loadRecharges();
		},
		// 进入第2步:开启手机扫码上传会话+轮询(截图在手机上时扫码直传)
		enterStep2() {
			this.rcStep = 2;
			this.startMupSession();
		},
		async startMupSession() {
			this.stopMupPoll();
			this.mupSid = '';
			try {
				const data = await mealApi.mupCreate();
				this.mupSid = data.sid;
				this.mupTimer = setInterval(this.pollMup, 3000);
			} catch (e) { /* http封装已弹错误提示,二维码区保持“加载中” */ }
		},
		async pollMup() {
			if (!this.mupSid || this.rcStep !== 2 || !this.rcVisible) { this.stopMupPoll(); return; }
			try {
				const data = await mealApi.mupStatus(this.mupSid);
				if (data && data.urls && data.urls.length) {
					this.rcProofUrl = String(data.urls[data.urls.length - 1]);
					this.$message.success('已收到手机上传的截图');
					this.stopMupPoll();
				} else if (data && data.alive === false) {
					this.mupSid = ''; // 会话过期,二维码消失
					this.stopMupPoll();
				}
			} catch (e) { /* 单次轮询失败静默,下轮重试 */ }
		},
		stopMupPoll() {
			if (this.mupTimer) {
				clearInterval(this.mupTimer);
				this.mupTimer = null;
			}
		},
		loadRecharges() {
			mealApi.myRecharges(this.currentEmpId).then((rows) => {
				this.rcList = rows || [];
			}).catch(() => { });
		},
		rcStatusText(r) {
			if (r.status === 1) return '已入账 ¥' + this.fmt(r.auditAmount != null ? r.auditAmount : r.amount);
			if (r.status === 2) return '已驳回' + (r.remark ? '：' + r.remark : '');
			return '待审核';
		},
		onRcProofOk(data) {
			this.rcProofUrl = data.originUrl;
			this.$message.success('截图已上传');
		},
		onRcProofFail() {
			this.$message.error('截图上传失败，请重试');
		},
		submitRecharge() {
			const amt = parseFloat(this.rcAmount);
			if (!amt || amt <= 0) { this.$message.error('请填写正确的付款金额'); return; }
			if (!this.rcProofUrl) { this.$message.error('请先上传付款截图'); return; }
			this.rcSubmitting = true;
			mealApi.recharge({ amount: amt, proofUrl: this.rcProofUrl, empId: this.currentEmpId }).then(() => {
				this.stopMupPoll();
				this.rcStep = 3;
				this.loadRecharges();
			}).catch(() => { }).finally(() => {
				this.rcSubmitting = false;
			});
		}
	},
	beforeDestroy() {
		this.stopMupPoll();
	}
}
</script>

<style scoped lang="scss">
.mc-wrap {
	padding: 0 4px;
}

.mc-head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;

	.mc-nav {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 18px;
		font-weight: 600;

		i {
			cursor: pointer;
			padding: 4px;
			border-radius: 4px;

			&:hover {
				background: #f0f0f0;
			}
		}

		.mc-today-btn {
			font-size: 13px;
			font-weight: 400;
			color: #409eff;
			cursor: pointer;
			padding: 2px 8px;
			border: 1px solid #b3d8ff;
			border-radius: 4px;

			&:hover {
				background: #ecf5ff;
			}
		}
	}

	.mc-balance {
		font-size: 16px;
		font-weight: 600;
		color: #67c23a;

		&.neg {
			color: #f56c6c;
		}
	}
}

.mc-emps {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;

	.mc-emps-label {
		font-size: 13px;
		color: #909399;
	}

	.mc-emp-tab {
		font-size: 14px;
		padding: 3px 14px;
		border-radius: 14px;
		border: 1px solid #dcdfe6;
		color: #606266;
		cursor: pointer;
		user-select: none;

		&:hover {
			border-color: #409eff;
			color: #409eff;
		}

		&.on {
			background: #409eff;
			border-color: #409eff;
			color: #fff;
		}
	}
}

.mc-tip {
	background: #fdf6ec;
	border: 1px solid #faecd8;
	color: #b88230;
	font-size: 13px;
	line-height: 1.6;
	padding: 8px 10px;
	border-radius: 6px;
	margin-bottom: 10px;
}

.mc-weeks {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	text-align: center;
	font-size: 13px;
	color: #909399;
	padding-bottom: 6px;
	border-bottom: 1px solid #ebeef5;
}

.mc-grid {
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	border-left: 1px solid #ebeef5;

	.mc-cell {
		min-height: 72px;
		border-right: 1px solid #ebeef5;
		border-bottom: 1px solid #ebeef5;
		padding: 4px 6px;

		&.dim {
			background: #fafafa;

			.mc-day {
				color: #c0c4cc;
			}
		}

		&.today .mc-day {
			display: inline-block;
			min-width: 22px;
			height: 22px;
			line-height: 22px;
			text-align: center;
			border-radius: 50%;
			background: #f56c6c;
			color: #fff;
		}

		.mc-day {
			font-size: 14px;
			color: #303133;
			margin-bottom: 4px;
		}

		.mc-meal {
			display: inline-block;
			font-size: 12px;
			line-height: 18px;
			padding: 0 6px;
			border-radius: 4px;
			margin: 1px 2px 1px 0;
			cursor: pointer;
			user-select: none;

			&.st-eat {
				background: #f0f9eb;
				color: #67c23a;
				cursor: default;
			}

			&.st-skip {
				background: #fef0f0;
				color: #f56c6c;
			}

			&.st-add {
				border: 1px dashed #c0c4cc;
				color: #909399;

				&:hover {
					border-color: #f56c6c;
					color: #f56c6c;
				}
			}

			&.st-past {
				color: #c0c4cc;
				cursor: default;
			}
		}
	}
}

.mc-foot {
	margin-top: 10px;
	font-size: 13px;
	color: #606266;

	.mc-sep {
		color: #dcdfe6;
		margin: 0 6px;
	}

	.mc-neg {
		color: #f56c6c;
	}
}

.mc-empty {
	text-align: center;
	color: #f56c6c;
	padding: 30px 0;
	font-size: 14px;
}

// ----- 自助充值 -----
.mc-recharge-btn {
	margin-left: 8px;
	font-size: 12px;
	padding: 2px 10px;
	border-radius: 12px;
	background: #67c23a;
	color: #fff;
	cursor: pointer;
	user-select: none;

	&:hover {
		background: #85ce61;
	}
}

.rc-body {
	text-align: center;

	.rc-qr {
		width: 260px;
		border-radius: 8px;
		border: 1px solid #ebeef5;
	}

	.rc-tip {
		margin: 12px 0;
		font-size: 14px;
		line-height: 1.9;
		color: #606266;
		text-align: left;

		&.warn {
			color: #e6a23c;
			text-align: center;
			font-weight: 600;
		}
	}

	.rc-field {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 12px;

		label {
			font-size: 14px;
			color: #606266;
			white-space: nowrap;
		}
	}

	.rc-upload {
		width: 100%;
		height: 120px;
		border: 1px dashed #c0c4cc;
		border-radius: 8px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 6px;
		color: #909399;
		font-size: 13px;
		cursor: pointer;
		overflow: hidden;
		margin-bottom: 12px;

		&:hover {
			border-color: #409eff;
			color: #409eff;
		}

		i {
			font-size: 26px;
		}

		.rc-proof {
			max-height: 118px;
			max-width: 100%;
			object-fit: contain;
		}
	}

	.rc-upload-row {
		display: flex;
		gap: 12px;
	}

	.rc-upload-col {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;

		.rc-upload {
			height: 140px;
			margin-bottom: 0;
		}

		.rc-mup {
			cursor: default;
		}

		.rc-mup-qr {
			width: 100%;
			height: 138px;
			object-fit: contain;
		}

		.rc-mup-tip {
			font-size: 12px;
			color: #c0c4cc;
			text-align: center;
			line-height: 1.6;
		}
	}

	.rc-col-tip {
		margin-top: 6px;
		margin-bottom: 12px;
		font-size: 12px;
		color: #909399;
		text-align: center;
		line-height: 1.5;
	}

	.rc-btn {
		width: 100%;
	}

	.rc-done {
		margin: 20px 0;
		font-size: 15px;
		line-height: 2;
		color: #67c23a;
	}

	.rc-history {
		margin-top: 14px;
		text-align: left;
		border-top: 1px solid #ebeef5;
		padding-top: 10px;

		.rc-his-title {
			font-size: 13px;
			color: #909399;
			margin-bottom: 6px;
		}

		.rc-his-row {
			display: flex;
			gap: 10px;
			font-size: 13px;
			line-height: 1.9;

			.rc-his-time {
				color: #909399;
				flex: 1;
			}

			.rc-his-amt {
				font-weight: 600;
			}

			.rc-his-st {
				&.st0 { color: #e6a23c; }
				&.st1 { color: #67c23a; }
				&.st2 { color: #f56c6c; }
			}
		}
	}
}
</style>
